<template>
  <v-content>
    <v-container :fluid="this.$vuetify.breakpoint.mdAndDown">
      <v-card class="pa-4">
        <h1 class="display-3 secondary--text font-weight-bold pl-2 mb-4">
          Upcoming Community Events Calendar
        </h1>

        <!-- Date Navigation -->
        <v-layout row wrap align-center class="mb-4 px-2">
          <v-btn icon @click="previousMonth">
            <v-icon>chevron_left</v-icon>
          </v-btn>
          <h2 class="display-1 primary--text mx-3">
            {{ currentMonthYear }}
          </h2>
          <v-btn icon @click="nextMonth">
            <v-icon>chevron_right</v-icon>
          </v-btn>
          <v-spacer />
          <v-btn color="primary" @click="goToToday">Today</v-btn>
        </v-layout>

        <!-- Loading State -->
        <v-layout v-if="loading" row justify-center class="py-5">
          <v-progress-circular indeterminate color="primary" />
        </v-layout>

        <!-- Error State -->
        <v-alert v-else-if="error" type="error" class="mb-4">
          {{ error }}
        </v-alert>

        <!-- Calendar Grid -->
        <div v-else class="calendar-grid">
          <!-- Day Headers -->
          <div class="calendar-header">
            <div
              v-for="day in weekDays"
              :key="day"
              class="calendar-header-cell primary--text"
            >
              {{ day }}
            </div>
          </div>

          <!-- Calendar Days -->
          <div class="calendar-body">
            <div
              v-for="(day, index) in calendarDays"
              :key="index"
              class="calendar-cell"
              :class="{
                'other-month': !day.isCurrentMonth,
                today: day.isToday,
                selected: isSelectedDay(day),
                'has-events': day.events.length > 0
              }"
              @click="selectDate(day)"
            >
              <div class="day-number">{{ day.date }}</div>
              <div class="day-events">
                <div
                  v-for="event in day.events.slice(0, 2)"
                  :key="event.id"
                  class="event-dot"
                  :title="event.title"
                >
                  {{ truncateTitle(event.title) }}
                </div>
                <div v-if="day.events.length > 2" class="more-events">
                  +{{ day.events.length - 2 }} more
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Selected Day Events List -->
        <v-card
          v-if="selectedDay && selectedDay.events.length > 0"
          class="mt-4 pa-3"
        >
          <h3 class="headline primary--text mb-3">
            Events for {{ formatSelectedDate }}
          </h3>
          <v-list>
            <v-list-tile
              v-for="event in selectedDay.events"
              :key="event.id"
              class="event-list-item"
              @click="selectEvent(event)"
            >
              <v-list-tile-content>
                <v-list-tile-title class="font-weight-bold">
                  {{ event.title }}
                </v-list-tile-title>
                <v-list-tile-sub-title>
                  <v-icon small class="mr-1">access_time</v-icon>
                  {{ formatEventTime(event) }}
                  <span v-if="event.location" class="ml-3">
                    <v-icon small class="mr-1">place</v-icon>
                    {{ event.location }}
                  </span>
                </v-list-tile-sub-title>
              </v-list-tile-content>
              <v-list-tile-action>
                <v-icon>chevron_right</v-icon>
              </v-list-tile-action>
            </v-list-tile>
          </v-list>
        </v-card>

        <!-- No Events Message for Selected Day -->
        <v-card v-else-if="selectedDay" class="mt-4 pa-3">
          <p class="subheading text-center grey--text">
            No events scheduled for {{ formatSelectedDate }}
          </p>
        </v-card>

        <!-- Selected Event Detail (Listing Card Style) -->
        <v-card v-if="selectedEvent" class="mt-4">
          <v-toolbar flat color="white">
            <v-btn icon @click="selectedEvent = null">
              <v-icon>arrow_back</v-icon>
            </v-btn>
            <v-toolbar-title class="secondary--text"
              >Event Details</v-toolbar-title
            >
          </v-toolbar>
          <v-card flat tile height="100%">
            <v-layout fill-height :column="$vuetify.breakpoint.smAndDown">
              <v-flex xs7 class="pa-0">
                <v-card flat tile color="white" min-height="100%" class="pa-4">
                  <h2 class="display-1 secondary--text">
                    {{ selectedEvent.title }}
                  </h2>
                  <p
                    class="subtitle-1 primary--text font-weight-medium mb-2 mt-2"
                  >
                    <v-icon small class="mr-1">event</v-icon>
                    {{ formatEventDateTime(selectedEvent) }}
                  </p>
                  <p v-if="parsedEventData.description" class="subheading">
                    {{ parsedEventData.description }}
                  </p>
                  <!-- Pillars/Shields -->
                  <div
                    v-if="
                      parsedEventData.pillars &&
                        parsedEventData.pillars.length > 0
                    "
                    class="mt-3"
                  >
                    <router-link
                      v-for="(shield, key) in SHIELDS"
                      :key="key"
                      :to="{ name: 'About', hash: `#${key}` }"
                    >
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-img
                            v-if="
                              parsedEventData.pillars.includes(shield.title)
                            "
                            :src="require(`../assets/${shield.imgsrc}`)"
                            class="shield-img d-inline-block"
                            v-on="on"
                          />
                        </template>
                        <span
                          >{{ shield.title }} (Click for more information)</span
                        >
                      </v-tooltip>
                    </router-link>
                  </div>
                </v-card>
              </v-flex>
              <v-flex xs5 class="pa-0">
                <v-card
                  flat
                  tile
                  color="grey lighten-3"
                  min-height="100%"
                  class="pa-4"
                >
                  <h6 class="title secondary--text">Contact</h6>
                  <p
                    v-if="parsedEventData.address"
                    class="body-1 primary--text overflow-wrap"
                  >
                    Address: {{ parsedEventData.address }}
                  </p>
                  <p
                    v-if="parsedEventData.phone"
                    class="body-1 primary--text overflow-wrap"
                  >
                    Tel: {{ parsedEventData.phone }}
                  </p>
                  <p
                    v-if="parsedEventData.email"
                    class="body-1 primary--text overflow-wrap"
                  >
                    Email: {{ parsedEventData.email }}
                  </p>
                  <a
                    v-if="parsedEventData.website && !kioskMode"
                    :href="parsedEventData.website"
                    class="body-1 primary--text overflow-wrap"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Website: {{ parsedEventData.website }}
                  </a>
                  <p
                    v-else-if="parsedEventData.website"
                    class="body-1 primary--text overflow-wrap"
                  >
                    Website: {{ parsedEventData.website }}
                  </p>
                  <div
                    v-if="parsedEventData.website && kioskMode"
                    style="text-align: center"
                  >
                    <div style="width: 200px; margin: auto">
                      <qr-code :text="parsedEventData.website" :size="200" />
                    </div>
                    Scan the QR code with your smart device's camera app for
                    more information
                  </div>
                  <p
                    v-if="parsedEventData.expiresOn"
                    class="body-1 primary--text overflow-wrap"
                  >
                    Expires: {{ formatExpiresOn(parsedEventData.expiresOn) }}
                  </p>
                  <!-- Show message if no contact info -->
                  <p v-if="!hasContactInfo" class="body-1 grey--text">
                    No contact information available for this event.
                  </p>
                </v-card>
              </v-flex>
            </v-layout>
          </v-card>
        </v-card>
      </v-card>
    </v-container>

    <div style="height: 30px">
      <!--spacer to ensure back button doesn't cover up content-->
    </div>
    <div
      v-if="kioskMode"
      class="hidden-sm-and-down"
      style="position: fixed; bottom: 50px; left: 0; z-index: 10"
    >
      <fma-back-button />
    </div>
  </v-content>
</template>

<script>
import FmaBackButton from "@/components/FmaBackButton.vue";
import QrCode from "vue-qrcode-component";
import { SHIELDS } from "../constants";
import { mapState, mapGetters } from "vuex";
import axios from "axios";

export default {
  components: {
    FmaBackButton,
    QrCode
  },
  data() {
    return {
      currentDate: new Date(),
      events: [],
      upcomingListings: [],
      loading: true,
      error: null,
      selectedDay: null,
      selectedEvent: null,
      weekDays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    };
  },
  computed: {
    ...mapState({ kioskMode: state => state.kioskMode }),
    ...mapGetters(["baseUrl"]),
    SHIELDS() {
      return SHIELDS;
    },
    currentMonthYear() {
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December"
      ];
      return `${
        months[this.currentDate.getMonth()]
      } ${this.currentDate.getFullYear()}`;
    },
    calendarDays() {
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();

      const firstDayOfMonth = new Date(year, month, 1);
      const lastDayOfMonth = new Date(year, month + 1, 0);

      const startDay = firstDayOfMonth.getDay();
      const daysInMonth = lastDayOfMonth.getDate();

      const days = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Previous month days
      const prevMonth = new Date(year, month, 0);
      const prevMonthDays = prevMonth.getDate();
      for (let i = startDay - 1; i >= 0; i--) {
        const date = prevMonthDays - i;
        const fullDate = new Date(year, month - 1, date);
        days.push({
          date,
          fullDate,
          isCurrentMonth: false,
          isToday: false,
          events: this.getEventsForDate(fullDate)
        });
      }

      // Current month days
      for (let date = 1; date <= daysInMonth; date++) {
        const fullDate = new Date(year, month, date);
        fullDate.setHours(0, 0, 0, 0);
        days.push({
          date,
          fullDate,
          isCurrentMonth: true,
          isToday: fullDate.getTime() === today.getTime(),
          events: this.getEventsForDate(fullDate)
        });
      }

      // Next month days to fill the grid
      const remainingDays = 42 - days.length; // 6 rows * 7 days
      for (let date = 1; date <= remainingDays; date++) {
        const fullDate = new Date(year, month + 1, date);
        days.push({
          date,
          fullDate,
          isCurrentMonth: false,
          isToday: false,
          events: this.getEventsForDate(fullDate)
        });
      }

      return days;
    },
    formatSelectedDate() {
      if (!this.selectedDay) return "";
      const options = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric"
      };
      return this.selectedDay.fullDate.toLocaleDateString("en-US", options);
    },
    parsedEventData() {
      if (!this.selectedEvent) return {};

      // If it's a listing, use its structured data directly
      if (this.selectedEvent.isListing) {
        return {
          description: this.selectedEvent.description,
          website: this.selectedEvent.website || "",
          email: this.selectedEvent.email || "",
          phone: this.selectedEvent.phone || "",
          address: this.selectedEvent.location || "",
          pillars: this.selectedEvent.shields || []
        };
      }

      // For Google Calendar events, parse the description
      const parsed = this.parseEventDescription(
        this.selectedEvent.description || ""
      );

      // Use event.location as fallback for address if not parsed from description
      if (!parsed.address && this.selectedEvent.location) {
        parsed.address = this.selectedEvent.location;
      }

      // Infer on-base status from address — matches "Fort Meade", "Ft Meade",
      // "Ft. Meade", etc. (case-insensitive)
      parsed.onbase = /\bfort\s+meade\b|\bft\.?\s*meade\b/i.test(
        parsed.address || ""
      );

      return parsed;
    },
    hasContactInfo() {
      const data = this.parsedEventData;
      return data.address || data.phone || data.email || data.website;
    }
  },
  mounted() {
    this.fetchEvents();
  },
  methods: {
    async fetchEvents() {
      this.loading = true;
      this.error = null;

      try {
        // Fetch events for a 3-month window around current month
        const year = this.currentDate.getFullYear();
        const month = this.currentDate.getMonth();

        const timeMin = new Date(year, month - 1, 1).toISOString();
        const timeMax = new Date(year, month + 2, 0).toISOString();

        // Fetch both Google Calendar events and manual listings in parallel
        const [calendarResponse, categoriesResponse] = await Promise.all([
          axios.get(`${this.baseUrl}/calendar`, {
            params: { timeMin, timeMax }
          }),
          axios.get(`${this.baseUrl}/categories`)
        ]);

        this.events = calendarResponse.data;

        // Find the "Upcoming Events" category and fetch its listings
        // API returns { data: [...] } structure
        const categories =
          categoriesResponse.data.data || categoriesResponse.data || [];
        const upcomingCategory = Array.isArray(categories)
          ? categories.find(
              c =>
                c.title.toLowerCase().includes("upcoming") &&
                c.title.toLowerCase().includes("event")
            )
          : null;

        if (upcomingCategory) {
          const listingsResponse = await axios.get(`${this.baseUrl}/listings`, {
            params: { category: upcomingCategory.id }
          });
          // Filter to only listings with event_on dates
          // API returns { data: [...] } structure
          const listings =
            listingsResponse.data.data || listingsResponse.data || [];
          this.upcomingListings = Array.isArray(listings)
            ? listings.filter(l => l.event_on)
            : [];
        } else {
          this.upcomingListings = [];
        }
      } catch (err) {
        console.error("Failed to fetch calendar events:", err);
        this.error = "Failed to load calendar events. Please try again later.";
      } finally {
        this.loading = false;
      }
    },
    getEventsForDate(date) {
      const dateStr = date.toISOString().split("T")[0];

      // Get Google Calendar events for this date
      const calendarEvents = this.events.filter(event => {
        const eventDate = new Date(event.start).toISOString().split("T")[0];
        return eventDate === dateStr;
      });

      // Get manual listings with event_on for this date
      const listingEvents = this.upcomingListings
        .filter(listing => {
          const eventDate = new Date(listing.event_on)
            .toISOString()
            .split("T")[0];
          return eventDate === dateStr;
        })
        .map(listing => this.listingToEvent(listing));

      return [...calendarEvents, ...listingEvents];
    },
    // Convert a listing to the event format used by the calendar
    listingToEvent(listing) {
      return {
        id: `listing-${listing.id}`,
        title: listing.title,
        description: listing.description,
        location: listing.address,
        start: listing.event_on,
        end: listing.event_on, // Listings don't have end time
        isAllDay: false,
        isListing: true,
        // Include listing-specific fields for the detail view
        shields: listing.shields,
        phone: listing.phone,
        dsnphone: listing.dsnphone,
        email: listing.email,
        website: listing.website
      };
    },
    selectDate(day) {
      this.selectedDay = day;
      this.selectedEvent = null; // Clear selected event when selecting a new day
    },
    selectEvent(event) {
      this.selectedEvent = event;
    },
    isSelectedDay(day) {
      if (!this.selectedDay) return false;
      return day.fullDate.getTime() === this.selectedDay.fullDate.getTime();
    },
    previousMonth() {
      this.currentDate = new Date(
        this.currentDate.getFullYear(),
        this.currentDate.getMonth() - 1,
        1
      );
      this.selectedDay = null;
      this.selectedEvent = null;
      this.fetchEvents();
    },
    nextMonth() {
      this.currentDate = new Date(
        this.currentDate.getFullYear(),
        this.currentDate.getMonth() + 1,
        1
      );
      this.selectedDay = null;
      this.selectedEvent = null;
      this.fetchEvents();
    },
    goToToday() {
      this.currentDate = new Date();
      this.selectedDay = null;
      this.selectedEvent = null;
      this.fetchEvents();
    },
    truncateTitle(title) {
      return title.length > 15 ? title.substring(0, 15) + "..." : title;
    },
    // Formats date/time to match FmaListing format: "Friday, February 20, 2026 at 1:00 PM"
    formatEventDateTime(event) {
      let start;

      // For all-day events, the date is just "YYYY-MM-DD" without time zone
      // Parsing this directly creates a UTC date which can shift to the wrong day locally
      // So we parse it as a local date instead
      if (event.isAllDay && event.start && !event.start.includes("T")) {
        const parts = event.start.split("-");
        start = new Date(parts[0], parts[1] - 1, parts[2]);
      } else {
        start = new Date(event.start);
      }

      const dateOptions = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric"
      };
      const timeOptions = {
        hour: "numeric",
        minute: "2-digit"
      };
      const formattedDate = start.toLocaleDateString("en-US", dateOptions);

      if (event.isAllDay) {
        return `${formattedDate} (All Day)`;
      }

      const formattedTime = start.toLocaleTimeString("en-US", timeOptions);
      return `${formattedDate} at ${formattedTime}`;
    },
    formatEventTime(event) {
      if (event.isAllDay) {
        return "All Day";
      }
      const start = new Date(event.start);
      const options = { hour: "numeric", minute: "2-digit" };

      // Listings only have a start time, no end time
      if (event.isListing) {
        return start.toLocaleTimeString("en-US", options);
      }

      const end = new Date(event.end);
      return `${start.toLocaleTimeString(
        "en-US",
        options
      )} - ${end.toLocaleTimeString("en-US", options)}`;
    },
    formatExpiresOn(dateStr) {
      if (!dateStr) return "";
      const parts = dateStr.split("-");
      const d = new Date(
        parseInt(parts[0]),
        parseInt(parts[1]) - 1,
        parseInt(parts[2])
      );
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      });
    },

    /**
     * Parses the Google Calendar event description to extract structured data.
     *
     * Expected format in Google Calendar description:
     *
     * [Main description text here]
     *
     * ---
     * Website: https://example.com
     * Email: contact@example.com
     * Phone: 555-123-4567
     * Address: 123 Main St, Building A
     * Pillars: Family, Emotional, Physical
     *
     * The "---" delimiter separates the main description from the structured fields.
     * All fields after the delimiter are optional.
     *
     * Handles malformed data gracefully:
     * - Extra spaces around colons ("Email :  test@test.com")
     * - No spaces ("Email:test@test.com")
     * - Wrong capitalization ("EMAIL: test@test.com", "emaiL: test@test.com")
     * - Extra dashes in delimiter ("----", "-----")
     * - Common typos in field names
     */
    parseEventDescription(rawDescription) {
      const result = {
        description: "",
        website: "",
        email: "",
        phone: "",
        address: "",
        pillars: [],
        expiresOn: null
      };

      if (!rawDescription) return result;

      // First, convert HTML to plain text
      let plainText = this.htmlToPlainText(rawDescription);

      // Find delimiter - match 3 or more dashes, with optional whitespace around them
      const delimiterMatch = plainText.match(
        /\n\s*-{3,}\s*\n|\n\s*-{3,}\s*$|^\s*-{3,}\s*\n|-{3,}/
      );

      if (!delimiterMatch) {
        // No structured data, entire description is the main description
        result.description = plainText.trim();
        return result;
      }

      // Split into main description and structured data
      const delimiterIndex = delimiterMatch.index;
      result.description = plainText.substring(0, delimiterIndex).trim();
      const structuredPart = plainText.substring(
        delimiterIndex + delimiterMatch[0].length
      );

      // Parse each line for key-value pairs
      const lines = structuredPart.split("\n");
      for (const line of lines) {
        // Find the first colon that's not part of a URL (http:// or https://)
        // We look for a colon that's followed by something other than //
        let colonIndex = -1;
        for (let i = 0; i < line.length; i++) {
          if (line[i] === ":") {
            // Check if this is part of http:// or https://
            if (line.substring(i, i + 3) === "://") {
              continue; // Skip this colon, it's part of a URL
            }
            colonIndex = i;
            break;
          }
        }

        if (colonIndex === -1) continue;

        // Normalize the key: trim whitespace, convert to lowercase, remove extra spaces
        const key = line
          .substring(0, colonIndex)
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "");
        const value = line.substring(colonIndex + 1).trim();

        // Skip empty values
        if (!value) continue;

        // Match against known field names (including common typos and variations)
        if (
          this.matchesFieldName(key, [
            "website",
            "web",
            "site",
            "url",
            "link",
            "webpage"
          ])
        ) {
          result.website = value;
        } else if (
          this.matchesFieldName(key, [
            "email",
            "e-mail",
            "mail",
            "emailaddress"
          ])
        ) {
          result.email = value;
        } else if (
          this.matchesFieldName(key, [
            "phone",
            "tel",
            "telephone",
            "phonenumber",
            "cell",
            "mobile",
            "contact"
          ])
        ) {
          result.phone = value;
        } else if (
          this.matchesFieldName(key, ["address", "addr", "location", "loc"])
        ) {
          result.address = value;
        } else if (
          this.matchesFieldName(key, [
            "expires",
            "expireson",
            "expiry",
            "expirationdate",
            "expiredate",
            "expiration"
          ])
        ) {
          result.expiresOn = this.parseFlexibleDate(value);
        } else if (
          this.matchesFieldName(key, [
            "pillars",
            "pillar",
            "shields",
            "shield",
            "resiliency",
            "category",
            "categories"
          ])
        ) {
          // Parse comma-separated pillars and normalize them
          const rawPillars = value
            .split(",")
            .map(p => p.trim())
            .filter(p => p);
          result.pillars = rawPillars
            .map(p => this.normalizePillarName(p))
            .filter(p => p);
        }
      }

      return result;
    },

    /**
     * Parses a flexible date string (m/d/yy, mm/dd/yyyy, etc.) to YYYY-MM-DD.
     * Returns null if the string cannot be parsed.
     */
    parseFlexibleDate(dateStr) {
      if (!dateStr) return null;
      const match = dateStr.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
      if (!match) return null;
      let month = parseInt(match[1], 10);
      let day = parseInt(match[2], 10);
      let year = parseInt(match[3], 10);
      if (year < 100) year += year < 50 ? 2000 : 1900;
      if (month < 1 || month > 12 || day < 1 || day > 31) return null;
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(
        2,
        "0"
      )}`;
    },

    /**
     * Converts HTML content to plain text.
     * - Converts <br>, <br/>, <br /> to newlines
     * - Extracts href values from <a> tags
     * - Strips all other HTML tags
     * - Decodes HTML entities
     */
    htmlToPlainText(html) {
      if (!html) return "";

      let text = html;

      // Convert <br> tags to newlines (handle all variations)
      text = text.replace(/<br\s*\/?>/gi, "\n");

      // Convert </p> and </div> to newlines
      text = text.replace(/<\/(p|div)>/gi, "\n");

      // Extract URLs from anchor tags: <a href="URL">text</a> -> URL
      text = text.replace(
        /<a\s+[^>]*href=["']([^"']*)["'][^>]*>[^<]*<\/a>/gi,
        "$1"
      );

      // Strip any remaining HTML tags
      text = text.replace(/<[^>]+>/g, "");

      // Decode common HTML entities
      text = text.replace(/&amp;/g, "&");
      text = text.replace(/&lt;/g, "<");
      text = text.replace(/&gt;/g, ">");
      text = text.replace(/&quot;/g, '"');
      text = text.replace(/&#39;/g, "'");
      text = text.replace(/&nbsp;/g, " ");

      // Clean up multiple consecutive newlines
      text = text.replace(/\n{3,}/g, "\n\n");

      return text.trim();
    },

    /**
     * Checks if a key matches any of the expected field names.
     * Uses fuzzy matching to handle minor typos.
     */
    matchesFieldName(key, expectedNames) {
      // Direct match
      if (expectedNames.includes(key)) {
        return true;
      }

      // Check if key is very close to any expected name (1 character difference)
      for (const expected of expectedNames) {
        if (this.isSimilar(key, expected)) {
          return true;
        }
      }

      return false;
    },

    /**
     * Simple similarity check - returns true if strings differ by at most 1 character
     * and are at least 3 characters long.
     */
    isSimilar(str1, str2) {
      if (str1.length < 3 || str2.length < 3) return false;
      if (Math.abs(str1.length - str2.length) > 1) return false;

      let differences = 0;
      const maxLen = Math.max(str1.length, str2.length);

      for (let i = 0; i < maxLen; i++) {
        if (str1[i] !== str2[i]) {
          differences++;
          if (differences > 1) return false;
        }
      }

      return true;
    },

    /**
     * Normalizes pillar names to match the expected format.
     * Handles common variations and typos.
     */
    normalizePillarName(input) {
      const normalized = input.toLowerCase().trim();

      // Map of variations to canonical names
      const pillarMap = {
        // Family
        family: "Family",
        families: "Family",
        fam: "Family",
        // Emotional
        emotional: "Emotional",
        emotion: "Emotional",
        emotions: "Emotional",
        mental: "Emotional",
        mentalhealth: "Emotional",
        // Physical
        physical: "Physical",
        phys: "Physical",
        fitness: "Physical",
        health: "Physical",
        // Spiritual
        spiritual: "Spiritual",
        spirit: "Spiritual",
        faith: "Spiritual",
        religious: "Spiritual",
        // Social
        social: "Social",
        community: "Social",
        soc: "Social"
      };

      // Direct lookup
      if (pillarMap[normalized]) {
        return pillarMap[normalized];
      }

      // Fuzzy match against known pillars
      const canonicalPillars = [
        "Family",
        "Emotional",
        "Physical",
        "Spiritual",
        "Social"
      ];
      for (const pillar of canonicalPillars) {
        if (this.isSimilar(normalized, pillar.toLowerCase())) {
          return pillar;
        }
      }

      // If no match found, return null (will be filtered out)
      return null;
    }
  }
};
</script>

<style scoped lang="less">
@import "../assets/global.less";

.calendar-grid {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.calendar-header {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background-color: #f5f5f5;

  .calendar-header-cell {
    padding: 12px;
    text-align: center;
    font-weight: bold;
    border-bottom: 1px solid #e0e0e0;
  }
}

.calendar-body {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.calendar-cell {
  min-height: 100px;
  border: 1px solid #e0e0e0;
  padding: 8px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }

  &.other-month {
    background-color: #fafafa;

    .day-number {
      color: #bdbdbd;
    }
  }

  &.today {
    background-color: #e8eef3;

    .day-number {
      background-color: #324e6b;
      color: white;
      border-radius: 50%;
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  &.selected {
    background-color: #e0e0e0;
  }

  // If both today and selected, keep today's blue background
  &.today.selected {
    background-color: #e3f2fd;
  }

  &.has-events {
    .day-number {
      font-weight: bold;
    }
  }
}

.day-number {
  font-size: 14px;
  margin-bottom: 4px;
}

.day-events {
  font-size: 11px;
}

.event-dot {
  background-color: #324e6b;
  color: white;
  padding: 2px 4px;
  border-radius: 2px;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.more-events {
  color: #757575;
  font-size: 10px;
}

.event-list-item {
  border-bottom: 1px solid #e0e0e0;
  cursor: pointer;

  &:hover {
    background-color: #f5f5f5;
  }

  &:last-child {
    border-bottom: none;
  }
}

.shield-img {
  width: 20%;
  max-width: 80px;
}

.overflow-wrap {
  overflow-wrap: break-word;
}
</style>
